import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { WorkoutMode } from '@kinomap-dev/ng-ui-lib/core';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { ColorService } from '../../services/color.service';
import { SectionsEditorService } from '../../services/sections-editor.service';
import { MinMaxStep, SectionChange, SectionModelType } from '../../types/section.type';
import { Duration } from '../../types/duration.type';
import { durationToSeconds } from '../../utils/duration.utils';
import { HorizontalPosition } from '../../types/position.type';
import {
  DEFAULT_SECTION_CADENCE,
  SECTION_CADENCE_MIN_MAX_STEP,
} from '../../configs/sections-editor/default-section.config';
import { AutoWidth } from '../../directives/auto-width.directive';

import { TranslateModule } from '@ngx-translate/core';
@Component({
  selector: 'app-section-editor-toolbar',
  imports: [TranslateModule, IconComponent, AutoWidth],
  templateUrl: './section-editor-toolbar.html',
  styleUrl: './section-editor-toolbar.scss',
})
export class SectionEditorToolbar {
  protected colorService: ColorService = inject(ColorService);
  protected sectionEditorService: SectionsEditorService = inject(SectionsEditorService);

  sections: InputSignal<SectionModelType[]> = input.required<SectionModelType[]>();
  sectionChange: OutputEmitterRef<SectionChange> = output<SectionChange>();
  duplicate: OutputEmitterRef<void> = output<void>();
  delete: OutputEmitterRef<void> = output<void>();
  move: OutputEmitterRef<HorizontalPosition> = output<HorizontalPosition>();

  protected readonly cadenceBounds: Readonly<MinMaxStep> = SECTION_CADENCE_MIN_MAX_STEP;
  /** Section de reference : ses valeurs sont affichees et servent de base aux deltas. */
  protected section: Signal<SectionModelType> = computed(() => this.sections()[0]);

  protected sectionDatas: Signal<{ type: WorkoutMode; value: number }[]> = computed(() =>
    this.sectionEditorService.sectionDatas(this.section(), this.sectionEditorService.workoutMode()),
  );

  protected onDataChange(type: WorkoutMode, value: string): void {
    if (Number.isNaN(parseInt(value))) throw new Error('data change is not a number');
    const current: number = this.sectionEditorService.sectionDataValue(
      this.section(),
      this.sectionEditorService.workoutMode(),
      type,
    );
    this.sectionChange.emit({ field: 'data', type, delta: parseInt(value) - current });
  }

  protected onTimeChange(field: 'minutes' | 'seconds', value: string): void {
    if (Number.isNaN(parseInt(value))) throw new Error('time change is not a number');
    const duration: Duration = { ...this.section().duration, [field]: parseInt(value) };
    const delta: number = durationToSeconds(duration) - durationToSeconds(this.section().duration);
    this.sectionChange.emit({ field: 'duration', delta });
  }

  /** Delta nul : les sections sans cadence prennent la cadence par defaut. */
  protected addCadence(): void {
    this.sectionChange.emit({ field: 'cadence', delta: 0 });
  }

  protected onCadenceChange(value: string): void {
    if (!parseInt(value)) return;
    const current: number = this.section().cadence ?? DEFAULT_SECTION_CADENCE;
    this.sectionChange.emit({ field: 'cadence', delta: parseInt(value) - current });
  }

  protected onCadenceBlur(value: string): void {
    if (!parseInt(value)) this.sectionChange.emit({ field: 'cadence', delta: undefined });
  }
}
